<template>
  <v-app class="tw-text-gray-900">
    <div
      v-if="isShowSessionSwitcher"
      class="tw-flex tw-gap-3 tw-items-center tw-bg-yellow-800 tw-text-white tw-px-6 tw-py-2 tw-sticky tw-top-0 tw-left-0 tw-w-full tw-z-50"
    >
      <Typography variant="subtitle-1">User session:</Typography>
      <div>
        <div
          class="tw-border tw-border-solid tw-border-white tw-bg-white tw-rounded-lg tw-px-3 tw-py-1.5 tw-flex tw-items-center tw-h-[38px]"
          :class="{ '!tw-border-red-700': emailError }"
        >
          <input
            v-model="userEmail"
            placeholder="Enter user email"
            class="tw-text-gray-900 tw-outline-none tw-bg-transparent tw-border-none tw-text-sm"
            @input="checkEmailValidate"
          />
        </div>
      </div>
      <Button size="small" :loading="tokenLoading" @click="onSessionChange"
        >Submit</Button
      >
    </div>
    <NavigationDrawer
      ref="navbar"
      :is-desktop="isDesktop"
      :is-pilot="isPilot"
      :current-project-id="currentProjectId"
      :current-project-settings="currentProjectSettings"
      :is-open-nav="isOpenNav"
      :isShowIntro="isShowIntro"
      :class="{ 'tw-mt-[54px]': isShowSessionSwitcher }"
      :isShowSessionSwitcher="isShowSessionSwitcher"
      @open-project="openProjectEvent"
      @change-nav="isOpenNav = !isOpenNav"
      @getTop="getTop"
      @isShowActions="onShowActions"
      @triggerProjectId="getCloneProject"
      @project-loaded="loading = false"
    />
    <Tooltip
      v-if="showCreateProjectTooltip"
      v-model="createProjectTooltipClone"
      triangle="left"
      x="355px"
      y="97px"
    >
      <div class="tooltip-body">
        <div class="tooltip__title">
          <span>Create your first project</span>
        </div>
        <div class="tooltip__content">
          Click here to start the process of creating a new project.
        </div>
      </div>
    </Tooltip>
    <AppBar
      :is-desktop="isDesktop"
      :is-open-nav="isOpenNav"
      :isShowIntro="isShowIntro"
      :isRecruiter="isRecruiter"
      :isPilot="isPilot"
      :isCurrentGolive="isCurrentGolive"
      :currentTab="newTabs[currentTab] ? newTabs[currentTab].key : ''"
      :class="{ '!tw-top-[54px]': isShowSessionSwitcher }"
      hideSearchBox
      @change-nav="isOpenNav = !isOpenNav"
      @add-read-scopes="showReadScopesModal = true"
      @triggerIntro="onTriggerInfo"
      @closeInfo="introStep += 1"
    >
    </AppBar>

    <v-main
      v-if="!loading && !isProjectsLoading && projectStatus !== 'loading'"
    >
      <div
        class="tw-bg-purple-50"
        :class="{
          'tw-min-h-screen-1': !isShowSessionSwitcher,
          'tw-min-h-screen-3': isShowSessionSwitcher,
        }"
      >
        <div
          v-if="projects.length"
          class="tw-bg-purple-50 tw-px-6 tw-pt-[28px] tw-w-full tw-text-gray-900"
        >
          <div
            class="tw-flex tw-justify-between tw-gap-10 tw-items-center tw-w-full"
          >
            <div class="tw-flex tw-gap-10 tw-items-center">
              <div>
                <Typography variant="headline-5">
                  {{ currentProjectSettings.project_name }}
                </Typography>
                <Typography
                  variant="body-2"
                  class="tw-flex tw-gap-1 tw-items-center"
                >
                  <div>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="tw-fill-purple-600"
                      :class="{
                        '!tw-fill-red-0':
                          currentProjectSettings.is_peq &&
                          !(calibrationMode && !isProjectProcessingView),
                      }"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M10.9572 19.9326C10.5979 15.5108 8.02575 11.7147 4.34973 9.65324C4.12235 10.3954 4 11.1834 4 12C4 16.0648 7.03161 19.4216 10.9572 19.9326ZM5.40215 7.47447C6.84423 5.37606 9.26142 4 12 4C14.7388 4 17.1561 5.37623 18.5981 7.47486C16.6071 8.45155 14.3678 8.99995 12.0005 8.99995C9.63289 8.99995 7.39338 8.45141 5.40215 7.47447ZM20.3352 6.47314C18.5439 3.77708 15.4795 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 10.8837 21.8171 9.81007 21.4796 8.80753C21.4797 8.80747 21.4799 8.8074 21.4801 8.80734C21.1998 7.97476 20.8129 7.19119 20.3356 6.47285C20.3355 6.47295 20.3353 6.47304 20.3352 6.47314ZM19.6504 9.65365C15.9748 11.7152 13.403 15.511 13.0437 19.9325C16.9688 19.4211 20 16.0645 20 12C20 11.1835 19.8777 10.3957 19.6504 9.65365ZM14.0005 11.9999C14.0005 13.1044 13.1051 13.9999 12.0005 13.9999C10.896 13.9999 10.0005 13.1044 10.0005 11.9999C10.0005 10.8953 10.896 9.99985 12.0005 9.99985C13.1051 9.99985 14.0005 10.8953 14.0005 11.9999Z"
                      />
                    </svg>
                  </div>
                  <div>Autopilot</div>
                </Typography>
              </div>
              <div class="tw-flex tw-gap-4 tw-items-center">
                <div>
                  <v-menu
                    v-model="settingsMenu"
                    transition="slide-y-transition"
                    bottom
                    nudge-bottom="55px"
                    class="selectMenuItem"
                    :disabled="isCurrentGolive"
                    :z-index="20"
                  >
                    <template v-slot:activator="{ on: menu }">
                      <v-tooltip
                        bottom
                        :disabled="!isNotAllowedClick"
                        max-width="15rem"
                      >
                        <template v-slot:activator="{ on: tooltip }">
                          <div v-on="{ ...tooltip, ...menu }">
                            <div
                              class="tw-cursor-pointer hover:tw-bg-highlight tw-p-2 tw-rounded-lg tw-transition-all tw-ease-in-out"
                            >
                              <div class="tw-flex tw-gap-2 tw-items-center">
                                <div>
                                  <img
                                    :src="MenuSettings"
                                    class="tw-w-5 tw-h-5"
                                  />
                                </div>
                                <Typography variant="button-2"
                                  >Settings</Typography
                                >
                              </div>
                            </div>
                          </div>
                        </template>
                        <span
                          >You don't have permissions to perform this
                          action</span
                        >
                      </v-tooltip>
                    </template>
                    <div
                      class="tw-bg-white tw-rounded-lg tw-py-2 tw-w-[245px] tw-shadow-shadow-2"
                    >
                      <div
                        v-for="(item, index) in steps"
                        :key="index"
                        class="tw-relative"
                      >
                        <div
                          v-if="item.children.length === 0"
                          class="tw-py-2 tw-px-4 hover:tw-bg-purple-100 tw-cursor-pointer"
                          @click="goToSettingPage(item.index)"
                        >
                          <Typography variant="body-2">{{
                            item.name
                          }}</Typography>
                        </div>
                        <div v-else>
                          <v-menu left open-on-hover nudge-left="245px">
                            <template v-slot:activator="{ on, attrs }">
                              <div v-bind="attrs" v-on="on">
                                <div
                                  class="tw-py-2 tw-px-4 hover:tw-bg-purple-100 tw-cursor-pointer"
                                  @click="goToSettingPage(item.index)"
                                >
                                  <Typography variant="body-2">{{
                                    item.name
                                  }}</Typography>
                                </div>
                              </div>
                            </template>
                            <div
                              class="tw-w-[245px] tw-bg-white tw-shadow-shadow-2 tw-rounded-lg"
                            >
                              <div
                                v-for="(subMenuItem, idx) in item.children"
                                :key="idx"
                                class="tw-py-2 tw-px-4 hover:tw-bg-purple-100 tw-cursor-pointer"
                                @click="
                                  goToSettingPage(item.children[idx].index)
                                "
                              >
                                {{ subMenuItem.name }}
                              </div>
                            </div>
                          </v-menu>
                        </div>
                      </div>
                    </div>
                  </v-menu>
                </div>
                <div>
                  <div
                    class="tw-cursor-pointer hover:tw-bg-highlight tw-p-2 tw-rounded-lg tw-transition-all tw-ease-in-out"
                    @click="
                      currentProjectId &&
                        $router.push(`/analytics?project=${currentProjectId}`)
                    "
                  >
                    <div class="tw-flex tw-gap-2 tw-items-center">
                      <div>
                        <img :src="AnalyticsIcon" class="tw-w-5 tw-h-5" />
                      </div>
                      <Typography variant="button-2">Analytics</Typography>
                    </div>
                  </div>
                </div>
                <div class="tw-relative tw-z-10">
                  <v-menu
                    bottom
                    nudge-bottom="35px"
                    :close-on-content-click="true"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        class="tw-cursor-pointer hover:tw-bg-highlight tw-p-2 tw-rounded-lg tw-transition-all tw-ease-in-out"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <div class="tw-flex tw-gap-2 tw-items-center">
                          <div>
                            <img :src="MoreIcon" class="tw-w-5 tw-h-5" />
                          </div>
                          <Typography variant="button-2">More</Typography>
                        </div>
                      </div>
                    </template>
                    <div class="tw-w-[200px]">
                      <Typography
                        variant="body-2"
                        class="tw-bg-white tw-shadow-shadow-2 tw-rounded-lg tw-overflow-hidden"
                      >
                        <div
                          v-if="
                            newTabs[currentTab] &&
                            newTabs[currentTab].key &&
                            ((newTabs[currentTab].key.toUpperCase() ===
                              'APPROVED' &&
                              this.currentProjectId &&
                              !this.isAutopilot) ||
                              newTabs[currentTab].key.toUpperCase() ===
                                'WARM_LEADS_AWAITING_INFO' ||
                              newTabs[currentTab].key.toUpperCase() ===
                                'AWAITING_INFO')
                          "
                          class="tw-py-3 tw-px-6 hover:tw-bg-purple-50 tw-cursor-pointer"
                          @click="importCandidates"
                        >
                          Import candidate
                        </div>
                        <div
                          class="tw-py-3 tw-px-6 hover:tw-bg-purple-50 tw-cursor-pointer"
                          @click="cloneProject"
                        >
                          Duplicate project
                        </div>
                        <v-tooltip
                          v-if="
                            !currentProject.outreach_pause &&
                            !currentProject.is_archived
                          "
                          right
                          max-width="242"
                          nudge-bottom="50"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              class="tw-py-3 tw-px-6 hover:tw-bg-purple-50 tw-cursor-pointer"
                              v-bind="attrs"
                              v-on="on"
                              @click="pauseProject(true)"
                            >
                              Pause project
                            </div>
                          </template>
                          <span>
                            Pausing the project will stop all future outreach to
                            candidates. You will still be able to manage your
                            existing pipeline. You can reactivate outreach
                            anytime by turning it on here.
                          </span>
                        </v-tooltip>
                        <v-tooltip
                          v-if="currentProject.outreach_pause"
                          bottom
                          :disabled="!(isPilot && isInCalibrationNewProject)"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              class="tw-py-3 tw-px-6 hover:tw-bg-purple-50 tw-cursor-pointer"
                              :class="{
                                'opacity-50':
                                  isPilot && isInCalibrationNewProject,
                              }"
                              v-bind="attrs"
                              v-on="on"
                              @click="pauseProject(false)"
                            >
                              Reactivate project
                            </div>
                          </template>
                          <span
                            >Hirefly will review and activate your project
                            within 24 hours!</span
                          >
                        </v-tooltip>
                        <v-tooltip
                          v-if="!currentProject.is_archived"
                          right
                          :max-width="242"
                          nudge-bottom="50"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              class="tw-py-3 tw-px-6 hover:tw-bg-purple-50 tw-cursor-pointer"
                              v-bind="attrs"
                              v-on="on"
                              @click="archiveProject(true)"
                            >
                              Archive project
                            </div>
                          </template>
                          <span>
                            Archiving the project marks it as inactive. You will
                            still be able to view the project in your Archived
                            folder. Your data will be saved and you can restore
                            the project from Archived anytime.
                          </span>
                        </v-tooltip>
                        <div
                          v-if="currentProject.is_archived"
                          class="tw-py-3 tw-px-6 hover:tw-bg-purple-50 tw-cursor-pointer"
                          @click="archiveProject(false)"
                        >
                          Restore from Archives
                        </div>
                      </Typography>
                    </div>
                  </v-menu>
                </div>
              </div>
            </div>

            <div>
              <div v-if="showCalibrationMode">
                <div class="tw-flex tw-gap-3 tw-items-center">
                  <div
                    v-if="isShowAdminCalibration"
                    class="tw-flex tw-gap-3 tw-items-center"
                  >
                    <Typography variant="body-2">
                      <span v-if="!isHideCalibrationToggle"
                        >Admin calibration
                        {{
                          payload.is_admin_in_auto_swipe_calibration
                            ? 'On'
                            : 'Off'
                        }}</span
                      >
                    </Typography>
                    <v-switch
                      v-if="!isHideCalibrationToggle"
                      v-model="payload.is_admin_in_auto_swipe_calibration"
                      :disabled="
                        currentProjectReviewCount === 0 ||
                        isShowIntro ||
                        isGolive ||
                        calibrationLoading
                      "
                      hide-details
                      class="!tw-mt-0"
                      @change="changeAdminCalibrationMode"
                    ></v-switch>
                  </div>
                  <div class="tw-flex tw-gap-3 tw-items-center">
                    <Typography variant="body-2">
                      <span v-if="!isHideCalibrationToggle"
                        >Calibration
                        {{
                          payload.is_in_auto_swipe_calibration ? 'On' : 'Off'
                        }}</span
                      >
                    </Typography>
                    <v-switch
                      v-if="!isHideCalibrationToggle"
                      v-model="payload.is_in_auto_swipe_calibration"
                      :disabled="
                        currentProjectReviewCount === 0 ||
                        isShowIntro ||
                        isGolive ||
                        calibrationLoading
                      "
                      hide-details
                      class="!tw-mt-0"
                      @change="changeCalibrationMode"
                    ></v-switch>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="tw-mt-5 tw-sticky tw-top-[61px] tw-z-[1] tw-px-6 tw-bg-purple-50"
          :class="{
            '!tw-top-[115px]': isShowSessionSwitcher,
            'tw-hidden': calibrationMode,
          }"
        >
          <v-tabs
            v-if="newTabs.length"
            ref="tabs-wrapper"
            hide-slider
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            show-arrows
            :value="currentTab"
            @change="!isShowIntro ? (currentTab = $event) : ''"
          >
            <v-tab
              v-for="(item, i) in newTabs"
              :key="item.key"
              :disabled="
                isCurrentGolive ||
                (isShowIntro &&
                  item.key !== 'warm_leads_hm' &&
                  item.key !== 'hiring_manager' && //TODO(jeremiah): clean this up after v2 rollout
                  item.key !== 'autopilot_recruiter' &&
                  item.key !== 'warm_leads_recruiter')
              "
              class="!tw-px-6"
            >
              <div v-if="tooltipTabs.includes(item.tab)">
                <v-tooltip bottom z-index="9999">
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      class="tw-flex tw-items-center tw-gap-1"
                      v-on="on"
                    >
                      <Typography
                        :variant="
                          item.key === newTabs?.[currentTab]?.key
                            ? 'subtitle-2'
                            : 'body-2'
                        "
                        tag="span"
                        class="tw-capitalize"
                        >{{ item.tab }}</Typography
                      >
                      <span
                        class="ml-2 tw-bg-purple-200 tw-text-purple-600 tw-font-inter tw-font-bold tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs"
                        >{{ getTabCount(item.key) }}</span
                      >
                      <span
                        v-if="i < newTabs.length - 1"
                        class="tw-absolute -tw-right-2 -tw-translate-y-1/2 tw-top-1/2"
                      >
                        <img :src="LeftTabIcon" class="tw-w-4 tw-h-4" />
                      </span>
                    </div>
                  </template>
                  <div class="tw-w-[275px] tw-text-white tw-py-2">
                    <Typography
                      v-if="item.tab === 'Interested'"
                      variant="body-2"
                    >
                      The Interested tab has candidates who are ready to
                      interview. When you click “Schedule Interview” button,
                      Hirefly will send an instant intro email to you and the
                      candidate and prompt the candidate to schedule time with
                      you. The candidate is then moved to the Scheduling tab.
                    </Typography>
                    <Typography
                      v-if="item.tab === 'Scheduling'"
                      variant="body-2"
                    >
                      The Scheduling tab contains candidates you approved in the
                      previous step. Hirefly has already sent an introductory
                      email, prompting them to schedule a time with you.
                    </Typography>
                    <Typography
                      v-if="item.tab === 'Interview'"
                      variant="body-2"
                    >
                      To keep track on the pipeline, you can either manually
                      move candidates to the Interview tab or automate the
                      process by using Hirefly’s scheduling link.
                    </Typography>
                    <Typography v-if="item.tab === 'Rejected'" variant="body-2">
                      The Rejected tab contains candidates you have rejected in
                      previous steps.
                    </Typography>
                  </div>
                </v-tooltip>
              </div>
              <div v-else class="tw-flex tw-items-center tw-gap-1">
                <Typography
                  :variant="
                    item.key === newTabs?.[currentTab]?.key
                      ? 'subtitle-2'
                      : 'body-2'
                  "
                  tag="span"
                  class="tw-capitalize"
                  :class="{
                    'tw-text-gray-900': item.key === newTabs?.[currentTab]?.key,
                    'tw-text-gray-700': item.key !== newTabs?.[currentTab]?.key,
                  }"
                  >{{ item.tab }}</Typography
                >
                <span
                  class="ml-2 tw-text-purple-600 tw-font-inter tw-font-bold tw-bg-purple-200 tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs"
                  >{{ getTabCount(item.key) }}</span
                >
                <span
                  v-if="i < newTabs.length - 1"
                  class="tw-absolute -tw-right-2 -tw-translate-y-1/2 tw-top-1/2"
                >
                  <img :src="LeftTabIcon" class="tw-w-4 tw-h-4" />
                </span>
              </div>
            </v-tab>
          </v-tabs>
        </div>
        <div
          v-if="calibrationMode && !isProjectProcessingView"
          class="tw-mt-5 tw-flex tw-items-center tw-gap-2 tw-bg-yellow-50 tw-py-2 tw-px-4 tw-rounded-lg tw-mx-6"
        >
          <div>
            <img :src="InfoIcon" />
          </div>
          <Typography variant="body-2">
            Calibration mode has been activated by Hirefly admins. Please review
            the following candidates to help train our AI and improve
            recommendations.
          </Typography>
        </div>

        <div v-if="isProjectProcessingView" class="tw-mt-5">
          <ProjectInProcessing />
        </div>
        <div v-else class="tw-mt-5">
          <v-tabs-items
            v-if="
              currentProject && !currentProject.is_archived && newTabs.length
            "
            v-model="currentTab"
            class="tw-w-full !tw-bg-purple-50"
          >
            <template>
              <v-tab-item
                v-for="item in newTabs"
                :key="item.key"
                class="tw-w-full"
              >
                <div
                  v-if="isReviewTab(item.key) || isOpen1x1Candidate"
                  class="tw-bg-purple-50 tw-w-full"
                >
                  <TabReview
                    :ref="item.tab.toUpperCase()"
                    :key="currentProjectId + item.tab"
                    :projectId="currentProjectId"
                    :tabs="newTabs"
                    :currentUserRole="currentUserRole"
                    :projectSettings="currentProjectSettings"
                    :tab="
                      item.key === 'review'
                        ? 'UNDER_REVIEW'
                        : item.key.toUpperCase()
                    "
                    :isInCalibrationNewProject="isInCalibrationNewProject"
                    :total-candidates="
                      currentProject[`candidates_${item.key}_count`] || 0
                    "
                    :autopilot_golive="
                      user.value.onboarding_tours &&
                      user.value.onboarding_tours.autopilot_golive === false
                    "
                    :isShowIntro="isShowIntro"
                    :isSelfServe="isProjectSelfServe"
                    :introStep="introStep"
                    :displayClipBoard="displayClipBoard"
                    :projectData="projectData"
                    :currentTab="
                      newTabs[currentTab] ? newTabs[currentTab].key : ''
                    "
                    :tabsNames="tabsNames"
                    :isCalibration="calibrationMode"
                    :isShowSessionSwitcher="isShowSessionSwitcher"
                    @changecalibration="onChangeCalibration"
                    @tour-finished="handleTourFinished"
                    @closeInfo="introStep += 1"
                    @showSettings="showSettingMenu"
                    @finishGolive="finishGolive"
                    @updateGolive="isCurrentGolive = $event"
                  />
                </div>
                <TabCompact
                  v-else
                  :ref="item.key.toUpperCase()"
                  :key="currentProjectId + item.tab"
                  :projectId="currentProjectId"
                  :tab="item.key.toUpperCase()"
                  :tabs="newTabs"
                  :isAutopilot="isPilot"
                  :isSelfServe="isUserSelfServe"
                  :isShowIntro2="isShowIntro"
                  :total-candidates="
                    currentProject[`candidates_${item.key}_count`] || 0
                  "
                  :displayClipBoard="displayClipBoard"
                  :projectData="projectData"
                  :currentTab="
                    newTabs[currentTab] ? newTabs[currentTab].key : ''
                  "
                  :isShowSessionSwitcher="isShowSessionSwitcher"
                />
              </v-tab-item>
            </template>
          </v-tabs-items>
          <div
            v-if="currentProject && currentProject.is_archived"
            class="w-full tw-bg-purple-50"
          >
            <div class="project-archived">
              <div class="project-archived__item">
                <div>
                  <img src="@/assets/img/archived-project.png" alt="" />
                </div>
                <div class="font-weight-bolder h-5 mt-9">
                  This project has been archived
                </div>
                <div class="mt-2">
                  You can reactivate the project by visiting Settings >> Project
                  Details
                </div>
              </div>
            </div>
          </div>
          <EmptyState
            v-if="!isLoading && isNotProjects && !isNewEmptyState"
            :image="EMPTY_STATE_NEW_USER_IMAGE"
            :text="$lang.Panel.null_title_first_time"
            :subtext="$lang.Panel.null_subtitle_first_time"
            class="tw-bg-purple-50"
          />
          <NewEmptyState
            v-if="!isLoading && isNotProjects && isNewEmptyState"
            :image="emptyImage"
            :text="emptyText"
            :content="emptyContent"
            class="tw-bg-purple-50"
          />
        </div>
      </div>
    </v-main>
    <v-banner v-if="isCanary" class="banner">
      <v-banner-text
        >You are currently using the Canary version of Hirefly. Please report
        any bugs to the Eng team. If you want to prevent redirection to Canary,
        use the following URL:
        <a href="https://www.hirefly.ai?no_redirect=true"
          >https://www.hirefly.ai?no_redirect=true</a
        >. Please only use no_redirect when a bug on Canary is preventing you
        from completing work.</v-banner-text
      >
    </v-banner>
    <!-- <v-alert
      v-if="!this.currentProjectId"
      id="alert"
      outlined
      color="primaryDark"
    >
      <div class="alert__inner">Click here to create your first project!</div>
    </v-alert> -->
    <v-dialog
      v-if="newTabs.length"
      v-model="showAuthenticationModal"
      width="25rem"
    >
      <v-card>
        <v-card-title class="headline grey lighten-2">
          Additional Permissions Required
        </v-card-title>

        <v-card-text class="mt-4">
          Looks like you have an active Autopilot project. Please log in again
          with Google to grant us permissions to manage your inbox.
        </v-card-text>

        <!-- <v-divider></v-divider> -->

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="authHandler"> OK </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showReadScopesModal" width="25rem">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          Additional Permissions
        </v-card-title>

        <v-card-text class="mt-4">
          Would you like Hirefly to automatically manage your inbox? This will
          ensure you don't send follow-ups to uninterested candidates. Hirefly
          will also automatically triage undelivered emails.
          <br /><br />
          If you'd like these benefits, please log in again with Google to grant
          us permissions to manage your inbox.
        </v-card-text>

        <!-- <v-divider></v-divider> -->

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grayDark" text @click="showReadScopesModal = false">
            Cancel
          </v-btn>
          <v-btn color="primary" text @click="authHandler"> Proceed </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="showArchivedPopup"
      v-model="showArchivedPopup"
      max-width="600px"
    >
      <ArchivedPopup
        :isArchived="isArchived"
        @close="showArchivedPopup = false"
        @submit="handleSubmitArchivedAndPause"
      />
    </v-dialog>
    <v-snackbar
      v-model="confirmSnackbar"
      color="white"
      max-width=""
      light
      centered
    >
      <strong class="d-block text-center success-message">
        <v-icon size="40" color="#4CAF50">mdi-check-circle</v-icon>
        {{ message }}
      </strong>
    </v-snackbar>
    <div
      v-if="loading || isProjectsLoading || projectStatus === 'loading'"
      class="position-fixed start-0 top-0 fill-height fill-width d-flex justify-center align-center loading-screen"
    >
      <v-progress-circular
        :size="72"
        :width="6"
        color="primary"
        indeterminate
        class="ma-auto"
      ></v-progress-circular>
    </div>
    <v-snackbar v-model="snackbar">{{ alertMessage }}</v-snackbar>
    <v-dialog v-model="importDialog" max-width="640">
      <ImportDialog
        :projectId="currentProjectId"
        :extra="
          newTabs[currentTab] &&
          newTabs[currentTab].key &&
          (newTabs[currentTab].key.toUpperCase() ===
            'WARM_LEADS_AWAITING_INFO' ||
            newTabs[currentTab].key.toUpperCase() === 'AWAITING_INFO')
        "
        @importDone="handleImportDone"
      />
    </v-dialog>
  </v-app>
</template>

<script>
import Config from '@/js/config';
import axios from 'axios';
import { get, put, post } from '@/js/api/sourcer';
import AppBar from '@/components/widgets/AppBar';
import NavigationDrawer from '@/components/widgets/NavigationDrawer';
import { mapState, mapGetters } from 'vuex';
import { normalizeSteps, sleep, checkIsEmail } from '@/utils/';
import NewEmptyState from '@/components/panel/components/NewEmptyState';
import LogoIcon5 from '@/assets/img/Artboard.svg';
import EditIcon from '@/assets/icons/edit-icon.svg';
import AnalyticsIcon from '@/assets/icons/analytics-icon.svg';
import MoreIcon from '@/assets/icons/more-icon.svg';
import LeftTabIcon from '@/assets/icons/left-tab-arrow.svg';
import MenuSettings from '@/assets/icons/settings-04.svg';
import ProjectInProcessing from '@/components/panel/ProjectInProcessing';
import {
  projectUpdateFlowAutopilot,
  projectUpdateFlowAutopilotV2,
  projectUpdateFlowAutopilotHireflyAdminV2,
} from '@/js/data';
import InfoIcon from '@/assets/icons/info-icon.svg';
import constantsV2 from '@/components/v2/project/constants';
import Typography from '@/components/v3/base/Typography';
import Button from '@/components/v3/base/Button';
import TabReview from './TabReview';
import TabCompact from './TabCompact';
import ArchivedPopup from './ArchivedPopup';
import EmptyState from './EmptyState';
import ImportDialog from './ImportDialog';

const tabsNames = {
  under_review: 'Review',
  approved: 'Approved',
  contacted: 'Contacted',
  interviewing: 'Interview',
  rejected: 'Rejected',
  not_interested: 'Not Interested',
  warm_leads_recruiter: 'Interested',
  warm_leads_hm: 'Interested',
  warm_leads_question: 'Needs Attention',
  manager_review: 'Manager Review',
  warm_leads_hm_approved: 'Scheduling',
  warm_leads_awaiting_info: 'Awaiting Reply',
};
const EMPTY_STATE_NEW_USER_IMAGE = require('@/assets/icons/rocket-icon.png');

const SELF_SERVE_TABS = [
  'WARM_LEADS_HM',
  'WARM_LEADS_QUESTION',
  'WARM_LEADS_HM_APPROVED',
  'INTERVIEWING',
  'CONTACTED',
  'WARM_LEADS_AWAITING_INFO',
  'REJECTED',
  'NOT_INTERESTED',
];
const AUTOPILOT_USER_TABS = [
  'WARM_LEADS_HM',
  'WARM_LEADS_HM_APPROVED',
  'INTERVIEWING',
  'REJECTED',
];
const AUTOPILOT_ADMIN_TABS = [
  'WARM_LEADS_RECRUITER',
  'WARM_LEADS_QUESTION',
  'WARM_LEADS_HM',
  'WARM_LEADS_HM_APPROVED',
  'INTERVIEWING',
  'CONTACTED',
  'WARM_LEADS_AWAITING_INFO',
  'REJECTED',
  'NOT_INTERESTED',
];
const AUTOPILOT_NEEDS_ATTENTION_TABS = [
  'WARM_LEADS_HM',
  'WARM_LEADS_QUESTION',
  'WARM_LEADS_HM_APPROVED',
  'INTERVIEWING',
  'REJECTED',
  'NOT_INTERESTED',
];
const NAVIGATOR_TABS = [
  'UNDER_REVIEW',
  'APPROVED',
  'CONTACTED',
  'INTERVIEWING',
  'REJECTED',
  'NOT_INTERESTED',
];

export default {
  name: 'PanelV2',
  components: {
    AppBar,
    NavigationDrawer,
    TabReview,
    TabCompact,
    ArchivedPopup,
    ImportDialog,
    EmptyState,
    NewEmptyState,
    Typography,
    Button,
    Tooltip: () => import('@/components/project/components/Tooltip'),
    ProjectInProcessing,
  },

  data() {
    const STEPS = this.getStepsForProjectType();
    const { ProjectSettingsFlow, ProjectCreationFlow } = constantsV2;
    return {
      currentTab: -1,
      loading: false,
      currentProjectId: undefined,
      oldProjectId: null,
      showAuthenticationModal: false,
      showReadScopesModal: false,
      steps: normalizeSteps(STEPS),
      isOpenNav: false,
      isDesktop: true,
      tabWidth: 0,
      isLoadedProject: false,
      newTabs: [],
      top: 0,
      isShowActions: false,
      triggerProjectId: null,
      triggerProjectName: null,
      confirmSnackbar: false,
      message: 'Project Copied',
      triggerPause: null,
      triggerArchive: null,
      isLoading: false,
      showArchivedPopup: false,
      isArchived: false,
      isShowIntro: false,
      introStep: 0,
      settingsMenu: false,
      menuItem: [],
      InfoIcon,
      isGolive: false,
      isCurrentGolive: false,
      EMPTY_STATE_NEW_USER_IMAGE,
      createProjectTooltipClone: true,
      tooltipTabs: ['Interested', 'Scheduling', 'Rejected', 'Interview'],
      contents: [
        'Interested tab has candidates who are ready to interview. When you click ’Schedule Interview’ button, on the candidate card, Hirefly will send an instant intro email to you and the candidate and prompt the candidate to schedule time with you. The candidate card is then sent to the Scheduling tab.',
      ],

      teamContents: [
        'Click on “Team” to add teammates to your project and configure your interview scheduling flow. ',
      ],

      leftInfoContents: [
        `<strong>Duplicate</strong> will make an exact copy of the project. If you prefer no settings changes, new candidates will arrive the next day. If you prefer to make changes to the settings, then you will see a set of candidates immediately.<br/>
        <strong>Pause</strong> will stop future outreach to candidates. You will still be able to manage your existing pipeline. You can reactivate project anytime from the dashboard.<br/>
        <strong>Archive</strong> will mark the project as inactive. You will still be able to view your project in the Archives folder. Your data will be saved and you can unarchive the project anytime from the dashboard.`,
      ],

      settingsContents: [
        'Visit Settings to make changes to your project, including your outreach templates and search preferences. Hirefly’s AI constantly tweaks your search, but you always have the ability to override the AI with your changes.',
      ],

      displayClipBoard: false,
      projectData: null,
      autopilotSteps: ProjectSettingsFlow,
      navigatorSteps: ProjectCreationFlow,
      hiddenChildStep: [
        'Search Preferences Process',
        'Search Preferences Tutorial',
        'KeywordsProcess',
      ],

      snackbar: false,
      alertMessage: '',
      projectIdWithLRO: '',
      EditIcon,
      AnalyticsIcon,
      MoreIcon,
      LeftTabIcon,
      isOpen1x1Candidate: false,
      tabsNames,
      payload: {
        is_in_auto_swipe_calibration: false,
        is_admin_in_auto_swipe_calibration: false,
      },

      importDialog: false,
      userEmail: '',
      tokenLoading: false,
      emailError: false,
      isSubmitted: false,
      MenuSettings,
      calibrationLoading: false,
    };
  },

  computed: {
    ...mapState({
      user: 'user',
      status: (state) => state.projects.status,
    }),

    ...mapGetters('user', {
      enableV2: 'getIsUserV2',
      isUserSelfServe: 'getIsUserSelfServe',
    }),

    isProjectProcessingView() {
      return this.currentProject.is_processing && !this.isRecruiter;
    },

    isShowSessionSwitcher() {
      const email = localStorage.getItem('email');
      if (
        email &&
        this.getEmailDomain(email) &&
        ['hirefly.ai', 'usetango.ai', 'hirefly-recruiting.com'].some(
          (item) => item === this.getEmailDomain(email)
        )
      ) {
        return true;
      }
      return false;
    },

    isShowAdminCalibration() {
      const email = this.user.value.email;
      if (
        email &&
        this.getEmailDomain(email) &&
        ['hirefly.ai', 'usetango.ai', 'hirefly-recruiting.com'].some(
          (item) => item === this.getEmailDomain(email)
        )
      ) {
        return true;
      }
      return false;
    },

    isUserSelfServe() {
      return this.$store.getters['user/getIsUserSelfServe'];
    },

    calibrationMode() {
      if (this.isInCalibrationNewProject) {
        return this.payload.is_in_auto_swipe_calibration;
      }
      if (this.isShowAdminCalibration) {
        return this.payload.is_admin_in_auto_swipe_calibration;
      }
      return this.payload.is_in_auto_swipe_calibration;
    },

    isInCalibrationNewProject() {
      return (
        this.currentProject.is_in_calibration || // TODO(jeremiah): delete after v2 migration
        this.currentProject.status === '6' ||
        (this.currentProject.status === '5' && this.isRecruiter)
      );
    },

    isAllowedClick() {
      return this.currentUserRole === 1;
    },

    isNotAllowedClick() {
      return this.currentUserRole === 3 || this.projects.length === 0;
    },

    emptyText() {
      return 'Welcome to Hirefly';
    },

    emptyImage() {
      return LogoIcon5;
    },

    emptyContent() {
      return 'Get started by creating your first project!<br/>Follow the tooltips to complete the setup.';
    },

    isNotProjects() {
      return !this.projects.length;
    },

    isProjectSelfServe() {
      return (
        (this.currentProjectSettings && // TODO(jeremiah): delete this line after V2 migration
          this.currentProjectSettings.is_self_serve) || // TODO(jeremiah): delete this line after V2 migration
        (this.currentProject && this.currentProject.type === '3')
      );
    },

    showCreateProjectTooltip() {
      if (
        !this.user.value.onboarding_tours ||
        this.user.value.onboarding_tours.project
      ) {
        return false;
      }
      return this.isUserSelfServe;
    },

    projects() {
      return this.$store.getters['projects/listProjects'];
    },

    currentProject() {
      if (!this.currentProjectId) {
        return {};
      }

      return (
        this.projects.find(
          (project) => project.project_id === this.currentProjectId
        ) || {}
      );
    },

    currentProjectSettings() {
      if (!this.currentProjectId) {
        return {};
      }

      return this.$store.getters['projects/settings/getSettingsByProjectId'](
        this.currentProjectId
      );
    },

    isCanary() {
      const { hostname } = window.location;
      return hostname.startsWith('hirefly-web-client-canary');
    },

    currentProjectReviewCount() {
      if (!this.currentProjectId) {
        return {};
      }
      return this.currentProject.candidate_batch_count
        ? this.currentProject.candidate_batch_count.UNDER_REVIEW
        : this.currentProject.candidates_review_count; // TODO(jeremiah): delete this after v2 migration
    },

    isPilot() {
      return (
        this.currentProject.in_auto_swipe_mode || // TODO(jeremiah): delete this after v2 migration
        this.currentProject.type === '1' ||
        this.currentProject.type === '3'
      );
    },

    isRecruiter() {
      return (
        this.currentProject.is_warmleads_recruiter || // TODO(jeremiah): delete this after v2 migration
        this.$store.getters['user/getIsSuperAdmin']
      );
    },

    isV2() {
      return this.$store.getters['user/getIsUserV2'];
    },

    // TODO(jeremiah): delete this method after v2 migration
    showNeedsAttention() {
      return this.currentProject.show_needs_attention;
    },

    currentUserRole() {
      const { collaborators } = this.currentProjectSettings;

      if (Array.isArray(collaborators) && collaborators.length) {
        const userEmail =
          this.user?.value?.email ?? localStorage.getItem('email');
        const filteredCollaborator = collaborators.filter(
          (c) => c.email === userEmail
        );

        return filteredCollaborator[0]
          ? parseInt(filteredCollaborator[0].role, 10)
          : -1;
      }
      return 1;
    },

    isSuperAdmin() {
      return this.$store.getters['user/getIsSuperAdmin'];
    },

    isNewEmptyState() {
      if (this.isUserSelfServe) return true;
      return false;
    },

    isHideCalibrationToggle() {
      const toggleCustomerCalibration =
        this.currentProjectSettings.show_calibration_toggle;
      return !this.isSuperAdmin && !toggleCustomerCalibration;
    },

    showCalibrationMode() {
      return (
        this.isPilot &&
        !this.isLoading &&
        (!this.isInCalibrationNewProject ||
          (this.isSelfServeProject && this.isInCalibrationNewProject))
      );
    },

    isProjectsLoading() {
      return this.$refs.navbar.loading;
    },

    projectStatus() {
      return (
        this.$store.getters['projects/settings/getStatus'](
          this.currentProjectId
        ) || ''
      );
    },

    userData() {
      return this.$store.getters['user/getUser'];
    },

    isAPV2() {
      return (
        this.userData?.experiments?.enable_ap_scoring_preferences_users ||
        this.userData?.experiments?.enable_ap_scoring_preferences_domain
      );
    },
  },

  watch: {
    calibrationMode() {
      this.calculateTabWidth();
    },

    currentProjectSettings() {
      this.setupStep();
      if (this.oldProjectId !== this.currentProjectId) {
        this.newTabs = [];
        setTimeout(() => {
          this.newTabs = this.getNewTabs();
          setTimeout(() => {
            if (!this.$route.query.candidate) {
              this.oldProjectId = this.currentProjectId;
              this.currentTab = -1;
            }
          }, 100);
        }, 10);
      }
      this.payload.is_in_auto_swipe_calibration =
        this.currentProjectSettings.is_in_auto_swipe_calibration;
      if (
        this.currentProjectSettings.is_admin_in_auto_swipe_calibration === null
      ) {
        this.payload.is_admin_in_auto_swipe_calibration =
          this.currentProjectSettings.is_in_auto_swipe_calibration;
      } else {
        this.payload.is_admin_in_auto_swipe_calibration =
          this.currentProjectSettings.is_admin_in_auto_swipe_calibration;
      }
    },

    currentProject() {
      this.setupStep();
      this.checkToShowHardRequirements();
      this.$store.commit('projects/SET_ACTIVE_PROJECT', {
        projectId: this.currentProjectId,
      });
      if (
        this.currentProject.status === '4' ||
        (this.currentProject.status === '5' && !this.isRecruiter)
      ) {
        if (this.projectIdWithLRO !== this.currentProjectId) {
          this.projectIdWithLRO = this.currentProjectId;
          this.increaseProgressBarPercent(0);
        }
        this.$store.dispatch('projects/waitForCalibration', {
          projectId: this.currentProjectId,
        });
      }
    },

    introStep(v) {
      if (v === 13) {
        // this.isShowIntro = false;
      }
    },
  },

  async created() {
    this.isLoading = true;
    this.loading = true;
    const user = await this.$store.dispatch('user/LOAD');
    if (user.missing_modify_scope) {
      this.showAuthenticationModal = true;
    }
    this.resizeHendler();
    this.newTabs = [];
    if (this.currentProject.project_id) {
      this.newTabs = this.getNewTabs();
      setTimeout(() => {
        this.currentTab = -1;
      }, 100);
    }

    window.addEventListener('resize', this.resizeHendler);

    this.checkAtcs();
    this.setupStep();
    this.checkToShowHardRequirements();

    this.payload.is_in_auto_swipe_calibration =
      this.currentProjectSettings.is_in_auto_swipe_calibration;
    if (
      this.currentProjectSettings.is_admin_in_auto_swipe_calibration === null
    ) {
      this.payload.is_admin_in_auto_swipe_calibration =
        this.currentProjectSettings.is_in_auto_swipe_calibration;
    } else {
      this.payload.is_admin_in_auto_swipe_calibration =
        this.currentProjectSettings.is_admin_in_auto_swipe_calibration;
    }

    this.loading = false;
    this.isLoading = false;
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHendler);
  },

  methods: {
    openSnackbar(e) {
      this.snackbar = true;
      this.alertMessage = e;
    },

    // check to show the review tab
    isReviewTab(key) {
      return (
        key === 'review' ||
        (this.isPilot &&
          (key === 'warm_leads_recruiter' || key === 'autopilot_recruiter') &&
          this.isRecruiter) ||
        (this.isPilot &&
          (key === 'warm_leads_question' || key === 'question') &&
          this.isRecruiter) ||
        (this.isPilot &&
          (key === 'warm_leads_hm' || key === 'hiring_manager') &&
          !this.isRecruiter)
      );
    },

    async checkToShowHardRequirements() {
      // TODO (pat): remove after v2 migration
      if (this.enableV2 || !this.currentProjectId || !this.isSuperAdmin) return;
      try {
        const res = await get(
          `${Config.env.API_URL}projects/hard_requirements?project_id=${this.currentProjectId}`
        );
        if (res.status !== 200 || !res.data.recruiter_notes) {
          return;
        }
        this.displayClipBoard = true;
        this.projectData = res.data;
      } catch (e) {
        this.openSnackbar('Hard requirements failed - please try again later.');
      }
    },

    finishGolive() {
      this.isGolive = false;
    },

    showSettingMenu(value) {
      if (!value) {
        setTimeout(() => {
          this.settingsMenu = true;
        }, 100);
        return;
      }
      this.isGolive = true;
      setTimeout(() => {
        this.settingsMenu = true;
      }, 100);
      setTimeout(() => {
        this.menuItem = [false, false, false, false, false, false];
        this.menuItem[value] = true;
      }, 200);
    },

    setupStep() {
      this.steps = normalizeSteps(this.getStepsForProjectType());
    },

    getStepsForProjectType() {
      if (this.isShowSessionSwitcher && this.isAPV2) {
        return projectUpdateFlowAutopilotHireflyAdminV2;
      }
      if (this.isAPV2) {
        return projectUpdateFlowAutopilotV2;
      }
      return projectUpdateFlowAutopilot;
    },

    getCloneProject(e) {
      this.triggerProjectName = `${e.name} - copy`;
      this.triggerProjectId = e.id;
      this.triggerPause = e.outreach_pause;
      this.triggerArchive = e.archive;
    },

    authHandler() {
      this.$gAuth.getAuthCode(
        this.authSuccessCallback,
        this.authErrorCallback,
        'https://www.googleapis.com/auth/gmail.modify'
      );
    },

    authSuccessCallback(resp) {
      const code = resp.code;
      const postBody = {};
      postBody.auth_provider = 'google';
      postBody.auth_code = code;
      postBody.redirect_uri = Config.env.REDIRECT_URI;
      // eslint-disable-next-line no-unused-expressions, no-sequences
      (postBody.scopes = ['gmail.modify']),
        (postBody.read_scopes = true),
        (axios.defaults.headers.post['Content-Type'] =
          'application/x-www-form-urlencoded');
      this.loading = true;
      this.showAuthenticationModal = false;
      this.showReadScopesModal = false;
      axios
        .post(`${Config.env.API_URL}users`, postBody)
        .then((response) => {
          this.$store
            .dispatch('auth/login', {
              session: response.data['X-Parse-Session-Token'],
              first_name: response.data.first_name,
              last_name: response.data.last_name,
              email: response.data.email,
              profile_picture: response.data.profile_picture,
            })
            .then(() => {});
        })
        .catch((error) => {
          console.error(error);
        });
    },

    authErrorCallback(error) {
      console.error('Error authenticating Google user');
      console.error(error);
      this.openSnackbar(
        'Error authenticating Google user - please try again later.'
      );
    },

    calculateTabWidth() {
      const component = this.$refs['tabs-wrapper'];

      if (!component) return;
      let q = 3;
      if (this.isDesktop && !this.isPilot) {
        q = 4;
      }
      if (this.isDesktop && this.isPilot && this.isRecruiter) {
        q = 5;
      } else if (this.isDesktop && this.isPilot && this.showNeedsAttention) {
        q = 4;
      }
      this.tabWidth = (component.$el.offsetWidth - 104) / q - (q - 1) / q + 1;
      this.isLoadedProject = true;
    },

    setIsDesktopValue() {
      this.isDesktop = window.innerWidth > 1170;
    },

    resizeHendler() {
      this.setIsDesktopValue();
      this.calculateTabWidth();
    },

    reloadCandidates(tab) {
      this.$store.dispatch('projects/candidates/loadCandidates', {
        projectId: this.currentProjectId,
        tab,
        force: true,
        id: this.$route.query.candidate,
      });
    },

    async openProjectEvent(e) {
      this.currentProjectId = e.projectId;
      if (
        !this.currentProjectSettings.project_name || // TODO(jeremiah): remove this line after V2 rollout
        (!this.currentProject.project_name && this.enableV2)
      ) {
        try {
          const res = await this.$store.dispatch(
            'projects/settings/loadSettings',
            {
              projectId: e.projectId,
            }
          );
          if (res === null && this.$route.query.project) {
            this.currentProjectId = null;
            this.$router.push('/panel');
          }
        } catch (err) {
          this.openSnackbar(
            'Load project settings failed - please try again later.'
          );
        }
      }
      const url = new URL(window.location);
      url.searchParams.set('project', e.projectId);
      window.history.pushState({}, '', url);

      this.newTabs = this.getNewTabs();

      if (e.candidateId) {
        // If a candidate is specified, load all candidates under the project.
        try {
          await Promise.all(
            this.newTabs.map((item) =>
              this.$store.dispatch('projects/candidates/loadCandidates', {
                projectId: e.projectId,
                tab:
                  item.key === 'review'
                    ? 'UNDER_REVIEW'
                    : item.key.toUpperCase(),
                id: this.$route.query.candidate,
              })
            )
          );
          this.newTabs = this.getNewTabs();
          this.currentTab = this.newTabs.findIndex((tab) => {
            const candidates = this.$store.getters[
              'projects/candidates/listCandidates'
            ](e.projectId, tab.key.toUpperCase());
            // eslint-disable-next-line no-underscore-dangle
            return candidates.find(
              (candidate) => candidate._id === e.candidateId
            );
          });

          // Call component routeCandidateId method.
          if (this.currentTab >= 0) {
            setTimeout(() => {
              const ref = this.newTabs[this.currentTab];
              const component = this.$refs[ref];
              if (component && component.routeCandidate) {
                this.$nextTick(() => {
                  component.routeCandidate(e.candidateId);
                  this.$eventHub.$emit('open-mail');
                });
              }
            }, 200);
          }
        } catch (err) {
          this.openSnackbar('Load candidates failed - please try again later.');
        }

        // Find the tab.
      }

      // If the tab is not specified, or not found, navigate to the first tab.
      if (this.currentTab < 0) {
        this.currentTab = 0;
      }
      // TODO(jeremiah): simplify these next 2 statements after v2 migration
      const warm_leads_hm_count = this.currentProject.candidate_batch_count
        ? this.currentProject.candidate_batch_count.HIRING_MANAGER
        : this.currentProject.candidates_warm_leads_hm_count;
      const warm_leads_question_count = this.currentProject
        .candidate_batch_count
        ? this.currentProject.candidate_batch_count.AUTOPILOT_QUESTION
        : this.currentProject.candidates_warm_leads_question_count;

      // If there are no interested candidates and there are needs attention candidates,
      // show the onboarding tour for the needs attention page.
      if (
        this.isUserSelfServe &&
        this.user.value.onboarding_tours &&
        !this.calibrationMode &&
        !this.user.value.onboarding_tours.self_serve_interested &&
        warm_leads_hm_count === 0 &&
        warm_leads_question_count > 0
      ) {
        this.currentTab = 1;
      }
    },

    openTab(tab) {
      this.currentTab = tab;
    },

    // TODO(jeremiah): simplify tab logic after v2 migration
    getTabCount(tab) {
      if (!this.currentProject.candidate_batch_count) {
        return this.currentProject[`candidates_${tab}_count`] || 0;
      }
      const batchCount = this.currentProject.candidate_batch_count;
      if (tab === 'review') {
        return batchCount.UNDER_REVIEW || 0;
      }

      const mappedName = tab.toUpperCase();
      return batchCount[mappedName] || 0;
    },

    getNewTabs() {
      // If a candidate is specified, load all candidates under the project.
      let tabs = [];
      if (this.isPilot) {
        // case 3
        tabs = AUTOPILOT_USER_TABS;
        if (
          this.currentProjectSettings.is_self_serve || // TODO(jeremiah): delete this line after v2 migration
          this.currentProject.type === '3'
        ) {
          tabs = SELF_SERVE_TABS;
        } else if (this.isRecruiter) {
          // case 2
          tabs = AUTOPILOT_ADMIN_TABS;
        } else if (this.showNeedsAttention) {
          tabs = AUTOPILOT_NEEDS_ATTENTION_TABS;
        }
      } else {
        // case 1
        tabs = this.currentProjectSettings.tab_order // TODO(jeremiah): delete this line after v2 migration
          ? this.currentProjectSettings.tab_order // TODO(jeremiah): delete this line after v2 migration
          : NAVIGATOR_TABS;
      }
      const tabsList = tabs.map((k) => {
        const t = k.toLowerCase();
        return {
          tab:
            t === 'warm_leads_hm' && this.isPilot && this.isRecruiter
              ? 'Manager Review'
              : tabsNames[t],
          key: t === 'under_review' ? 'review' : t,
        };
      });
      return tabsList;
    },

    async handleTourFinished(e) {
      const { onboarding_tours } = this.user.value || {};
      onboarding_tours[e] = true;
      try {
        await put(`${Config.env.API_URL}users/settings`, {
          onboarding_tours,
        });
      } catch (err) {
        this.openSnackbar('Update user failed - please try again later.');
      }
    },

    getTop() {
      this.top = `${
        document.getElementsByClassName('selected-item').length
          ? document
              .getElementsByClassName('selected-item')[0]
              .getBoundingClientRect().top
          : 0
      }px`;
    },

    onShowAction(e) {
      this.isShowActions = e;
    },

    async cloneProject() {
      if (this.loading) return;
      this.loading = true;
      this.isShowActions = false;
      this.isLoading = true;
      try {
        this.$store
          .dispatch('projects/copy', {
            project_id: this.currentProjectId,
            project_copy_name: this.triggerProjectName,
          })
          .then(async (response) => {
            await this.$store.dispatch('projects/LOAD', { force: true });
            this.message =
              'Project Copied. Please change your search preferences to get a new batch of candidates.';
            setTimeout(() => {
              this.$router.push(`/panel?project=${response.data}#no-scroll`);
              this.confirmSnackbar = true;
            }, 2000);
          })
          .catch((e) => {
            this.openSnackbar('Load projects failed - please try again later.');
            console.log(e);
          })
          .finally(() => {
            this.loading = false;
            this.isLoading = false;
          });
      } catch (e) {
        this.openSnackbar('Load projects failed - please try again later.');
      }
    },

    handleSubmitArchivedAndPause(params) {
      this.showArchivedPopup = false;
      if (this.isArchived) {
        this.submitArchiveProject(true, params);
      } else {
        this.submitPauseProject(true, params);
      }
    },

    async pauseProject(value) {
      if (this.isPilot && this.isInCalibrationNewProject) return;
      if (value) {
        this.showArchivedPopup = true;
        this.isArchived = false;
      } else {
        this.submitPauseProject(value);
      }
    },

    async submitPauseProject(value, params) {
      if (this.loading) return;
      this.loading = true;
      this.isLoading = true;
      this.isShowActions = false;
      this.$router.push('/panel#');
      try {
        // TODO(jeremiah): clean this up after v2 rollout
        if (this.isV2) {
          await this.$store.dispatch('projects/settings/pauseProject', {
            projectId: this.currentProjectId,
            shouldPause: value,
          });
        } else {
          await this.$store.dispatch('projects/settings/updateProject', {
            projectId: this.currentProjectId,
            project: {
              outreach_pause: value,
              ...params,
            },
          });
        }

        this.message = value
          ? 'Your Project has been paused!'
          : 'Project reactivated';
        const id = this.currentProjectId;
        setTimeout(() => {
          this.$router.push(`/panel?project=${id}`);
          this.loading = false;
          this.isLoading = false;
          this.confirmSnackbar = true;
        }, 2000);
      } catch (err) {
        this.openSnackbar('Project update failed - please try again later.');
        this.loading = false;
        this.isLoading = false;
      }

      // await this.$store.dispatch('projects/LOAD', { force: true });
    },

    async archiveProject(value) {
      if (value) {
        this.showArchivedPopup = true;
        this.isArchived = true;
      } else {
        this.submitArchiveProject(value);
      }
    },

    async submitArchiveProject(value, params) {
      if (this.loading) return;
      this.loading = true;
      this.isLoading = true;
      this.isShowActions = false;
      this.$router.push('/panel#');
      try {
        // TODO(jeremiah): clean this up after v2 rollout
        if (this.isV2) {
          await this.$store.dispatch('projects/settings/archiveProject', {
            projectId: this.currentProjectId,
            shouldArchive: value,
          });
        } else {
          await this.$store.dispatch('projects/settings/updateProject', {
            projectId: this.currentProjectId,
            project: {
              archive: value,
              is_archived: value,
              ...params,
            },
          });
        }

        // await this.$store.dispatch('projects/LOAD', { force: true });
        this.message = value
          ? 'Your Project has been archived!'
          : 'Project restored from archives';
        const id = this.currentProjectId;
        setTimeout(() => {
          this.$router.push(`/panel?project=${id}`);
          this.loading = false;
          this.confirmSnackbar = true;
          this.isLoading = false;
        }, 2000);
      } catch (err) {
        this.openSnackbar('Project update failed - please try again later.');
        this.loading = false;
        this.isLoading = false;
      }
    },

    checkAtcs() {
      const session = localStorage.getItem('session');
      axios.defaults.headers.post['X-Parse-Session-Token'] = session;
      axios.post(`${Config.env.API_URL}projects/check_atcs`).then((data) => {
        console.log(data);
      });
    },

    closeHandler() {},
    onTriggerInfo() {
      if (this.isProjectSelfServe) {
        if (this.currentTab >= 0) {
          let component;
          if (this.currentTab === 0) {
            try {
              component = this.$refs.INTERESTED[0];
            } catch {
              return;
            }
          } else {
            const ref = this.newTabs[this.currentTab];
            component = this.$refs[ref];
          }
          if (component && component.showTooltipTour) {
            component.showTooltipTour();
          }
        }
        return;
      }
      this.isShowIntro = !this.isShowIntro;
      this.introStep = 0;
    },

    onShowActions(e) {
      this.isShowActions = e;
    },

    goToSettingPage(e) {
      this.$router.push({
        path: `/projects/edit/${this.currentProjectId}`,
        query: { tab: e },
      });
    },

    async increaseProgressBarPercent(step) {
      if (step >= 120) {
        return;
      }
      const percentWaited = (step / 120) * 100;
      this.$store.commit('projects/candidates/SET_PROGRESS', {
        projectId: this.currentProjectId,
        progress: percentWaited,
      });
      await sleep(1000);
      await this.increaseProgressBarPercent(step + 1);
    },

    async changeCalibrationMode() {
      if (this.currentProjectReviewCount === 0) {
        return;
      }
      this.calibrationLoading = true;
      this.currentTab = 'review';
      const project = {};
      project.is_in_auto_swipe_calibration =
        this.payload.is_in_auto_swipe_calibration;
      project.is_admin_in_auto_swipe_calibration =
        this.payload.is_admin_in_auto_swipe_calibration;
      if (
        (!this.candidates || !this.candidates.length) &&
        this.isInCalibrationNewProject
      ) {
        project.is_in_calibration = false;
      }
      this.$store.dispatch('projects/settings/updateProject', {
        projectId: this.currentProjectId,
        project,
      });
      this.calibrationLoading = false;
    },

    async changeAdminCalibrationMode() {
      if (this.currentProjectReviewCount === 0) {
        return;
      }
      this.calibrationLoading = true;
      this.currentTab = 'review';
      const project = {};
      project.is_admin_in_auto_swipe_calibration =
        this.payload.is_admin_in_auto_swipe_calibration;
      if (
        (!this.candidates || !this.candidates.length) &&
        this.isInCalibrationNewProject
      ) {
        project.is_in_calibration = false;
      }
      await this.$store.dispatch('projects/settings/updateProject', {
        projectId: this.currentProjectId,
        project,
      });
      this.calibrationLoading = false;
    },

    importCandidates() {
      this.importDialog = true;
    },

    handleImportDone() {
      this.importDialog = false;
    },

    async onSessionChange() {
      this.isSubmitted = true;
      const isValid = this.checkEmailValidate();
      if (isValid) return;
      this.tokenLoading = true;
      const data = new FormData();
      data.append('command', '/session-token');
      data.append('text', this.userEmail);
      try {
        const res = await post(`${Config.env.API_URL}slack/commandin`, data);
        const token = res.data.text.replaceAll('Session token: ', '');
        if (token === 'None' || token === 'Command failed.') {
          this.openSnackbar('Could not find that user’s session');
        } else {
          localStorage.setItem('session', token);
          await this.$router.push({ path: '/' });
          window.location.reload();
        }
      } catch (e) {
        this.openSnackbar('Could not find that user’s session');
      }
      this.tokenLoading = false;
    },

    checkEmailValidate() {
      if (!this.isSubmitted) return false;
      if (this.userEmail === '') {
        this.emailError = true;
      } else if (
        !checkIsEmail(this.userEmail) ||
        checkIsEmail(this.userEmail) === 'Invalid e-mail.'
      ) {
        this.emailError = true;
      } else {
        this.emailError = false;
      }
      return this.emailError;
    },

    getEmailDomain(email) {
      if (!email.includes('@')) return null; // Validate the format
      return email.split('@')[1];
    },

    onChangeCalibration(val) {
      this.payload.is_in_auto_swipe_calibration = val;
      this.payload.is_admin_in_auto_swipe_calibration = val;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/variables.scss';
@import '@/components/project/components/main.scss';

.tabs-container {
  margin: 0;
  background: white;
  box-shadow: 0 1px 2px -3px rgba(0, 0, 0, 0.06), 0 1px 3px 0 rgba(0, 0, 0, 0.3);

  .c-tab {
    flex: 0 0 calc(100% - 288px);
    max-width: calc(100% - 288px);
    background-color: white;
  }
  .tabs {
    background-color: white;

    &::v-deep .v-item-group {
      height: 100%;

      .v-slide-group__next {
        border-left: 1px solid rgba(0, 0, 0, 0.12);
      }
      .v-slide-group__prev {
        border-right: 1px solid rgba(0, 0, 0, 0.12);
      }
    }
  }
}

.tab-item {
  padding: 0 7px;
  font-size: 16px;
  font-weight: normal;
  letter-spacing: 0.1px;
  text-transform: capitalize;
  color: $black !important;
  position: relative;
}
.tab-item.v-tab--active {
  font-weight: 500;
  background-color: #f3f4f6;
}
.tab-item span {
  color: $gray-light;
}
.tab-item.v-tab--active span {
  font-weight: normal;
  color: $black;
}

.hidden-tab {
  max-width: 0 !important;
  min-width: 0 !important;
}

.tab-btns__icons {
  padding: 6px 16px;
  width: 100%;
  height: 100%;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
.tab-btns__icons .btn {
  padding: 0;

  &.more-btn {
    .more-options {
      position: absolute;
      top: calc(100% + 12px);
      right: -35px;
      padding: 10px 0 30px;
      background-color: white;
      border-radius: 4px;
      box-shadow: -2px 3px 9px 4px rgba(0, 0, 0, 0.2);

      .more-options__bg {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        z-index: -1;
        cursor: default;
      }
      .more-options__inner {
        max-width: 400px;
        min-width: 270px;
        z-index: 21;

        .more-options__items {
          margin: 0;
          padding: 0;

          .more-options__item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 6px 9px 6px 30px;
            list-style: none;
            cursor: pointer;

            &:hover {
              background-color: #f3f4f6;

              span {
                color: $black;
              }
            }
            span {
              font-size: 14px;
              font-weight: normal;
              line-height: normal;
              letter-spacing: 0.44px;
              color: $gray-dark;
            }
          }
        }
      }
    }
  }
}
.tab-btns__icons .btn span {
  margin-top: 3px;
  text-transform: capitalize;
  font-size: 10px;
  letter-spacing: normal;
  color: $primary-dark;
}
.success-message {
  font-size: 20px;
}
.tab-btns {
  flex: 0 0 288px;
  height: inherit;
  background-color: white;
}

#alert {
  position: absolute;
  top: 127px;
  width: 200px;
  margin-left: 20rem;
  padding: 0 !important;
  border-width: 2px !important;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-left-radius: 0px;
  border-top-left-radius: 0px;
  border-left: none !important;
  font-weight: 500;

  .alert__inner {
    position: relative;
    width: 100%;
    height: 100%;
    padding: 10px 20px;
    text-align: center;
    color: $primary-dark;
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -25px;
    bottom: 0;
    margin: auto;
    width: 52px;
    height: 51px;
    border: 2px solid $primary-dark;
    border-bottom-left-radius: 16px;
    border-top-color: transparent;
    border-right-color: transparent;
    transform: rotate(45deg);
  }
}
.project-archived {
  height: 100%;
  display: flex !important;
  width: 100%;
  align-items: center;
  justify-content: center;
}
.w-full {
  width: 100%;
  height: 100%;
}
.project-archived__item {
  text-align: center;
  color: #a1a5a6;
}
.calibrating-text {
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 2px;
  color: $primary-dark;
}
.calibrating-count-text {
  font-weight: 500;
  font-size: 12px;
  color: $primary-dark;
}
.left-nav-content {
  position: absolute;
  left: 230px;
  background: #fff;
  box-shadow: -1px 2px 6px rgba(0, 0, 0, 0.36);
  max-width: 225px;
  width: 100%;
  display: block;
  z-index: 100;
  padding: 10px 0;
  &.show-intro {
    width: 10px;
    box-shadow: none;
    background-color: transparent;
  }
  ::v-deep .v-list-item {
    min-height: 0px;
    padding: 7px 12px;
    &.active,
    &:hover {
      background: #f3f4f6;
    }
  }
  ::v-deep .v-list-item__icon {
    margin: 0 10px 0 0;
  }
}
.loading-screen {
  z-index: 999;
}

.cursor-pointer {
  cursor: pointer;
  &:hover {
    background: #efefef !important;
    font-weight: 500 !important;
    font-size: 14px;
  }
}

.selectMenuItem {
  min-width: 255px !important;

  & .menuHeader {
    font-size: 16px;
  }
}
.menu-divider {
  margin-top: 0px !important;
}
.is-in-calibration {
  position: absolute;
  display: flex;
  justify-content: center;
  width: 100%;
  text-align: center;
}
.is-calibration-on {
  z-index: 100;
  background-color: inherit;
}
.opacity-50 {
  opacity: 0.5;
}

.team-position {
  position: absolute;
  right: 70%;
  top: 6px;
  margin-right: -48px;
}
.setting-position {
  position: absolute;
  top: 6px;
  right: 75px;
}
.project-info {
  position: absolute;
  top: -100px;
  left: 242px;
  &.my-project {
    top: 275px;
  }
  &.shared-project {
    top: 324px;
  }
  &.paused-project {
    top: 372px;
  }
  &.archived-project {
    top: 418px;
  }
}
.project-info-position {
  position: absolute;
  left: 100%;
}
@media only screen and (max-width: 1170px) {
  .project-info-position {
    display: none;
  }
}
.left-nav-content .v-list-item__content {
  .v-item-group {
    width: 100%;
  }
}
.overlay {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.15);
  z-index: 1;
}
.z-10 {
  z-index: 10;
}
.nav-overlay {
  &::v-deep {
    .v-slide-group__next,
    .v-slide-group__prev {
      position: relative;
      &::before {
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.15);
        z-index: 1;
        content: '';
      }
    }
  }
}
.list-active {
  background-color: #e9e9e9;
  font-weight: bold;
}
.tab-disabled {
  opacity: 1;
  .overlay {
    cursor: default;
  }
  &::before {
    display: none;
  }
}
.btn {
  &::v-deep {
    .v-btn__content {
      flex-flow: column;
    }
  }
}
.main-tabs {
  &::v-deep {
    .v-tab {
      border-right: 1px solid rgba(0, 0, 0, 0.12);
      letter-spacing: normal;
    }
  }
}
.tooltip-h {
  z-index: 20;
}

.banner {
  padding-left: 241px;
  font-size: 12px;
  background-color: #fffed2 !important;

  ::v-deep .v-banner__wrapper {
    padding: 8px !important;
  }
  ::v-deep .v-banner__text {
    text-align: center;
  }
}
::v-deep {
  .theme--light.v-tabs > .v-tabs-bar {
    background-color: transparent;
    .v-tab--active {
      color: #212121;
      font-weight: 600;
      position: relative;
      &::after {
        background-color: #5656b2;
        content: '';
        height: 3px;
        position: absolute;
        bottom: -3px;
        left: 0px;
        width: 100%;
        top: auto;
      }
    }
  }
  .v-slide-group__content {
    border-bottom: 3px solid #e0e0e0;
  }
}
::v-deep {
  .v-tooltip__content {
    background: #616161 !important;
    opacity: 1 !important;
  }
  .v-tab {
    letter-spacing: normal;
  }
  .v-window,
  .v-window__container {
    overflow: unset !important;
  }
  .v-window-item {
    box-shadow: none !important;
  }
}
</style>
